import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  FaEnvelope, 
  FaBell, 
  FaUsers, 
  FaComments, 
  FaCog, 
  FaUserEdit, 
  FaShieldAlt,
  FaSearch,
  FaMobile,
  FaLock,
  FaHeart,
  FaEye,
  FaFlag,
  FaChevronDown,
  FaChevronUp
} from 'react-icons/fa';

const FAQ = () => {
  const [expandedSection, setExpandedSection] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const faqSections = [
    {
      id: 'getting-started',
      title: 'Erste Schritte',
      icon: <FaUsers className="text-primary" />,
      questions: [
        {
          q: 'Wie erstelle ich einen Account?',
          a: 'Klicken Sie auf "Registrieren" in der oberen Navigation. Geben Sie Ihren gewünschten Benutzernamen, eine gültige E-Mail-Adresse und ein sicheres Passwort ein. Nach der Registrierung erhalten Sie eine Bestätigungs-E-Mail.'
        },
        {
          q: 'Wie logge ich mich ein?',
          a: 'Klicken Sie auf "Anmelden" und geben Sie Ihre E-Mail-Adresse oder Ihren Benutzernamen sowie Ihr Passwort ein. Sie können "Angemeldet bleiben" aktivieren, um automatisch eingeloggt zu bleiben.'
        },
        {
          q: 'Wie kann ich mein Passwort zurücksetzen?',
          a: 'Klicken Sie auf "Passwort vergessen?" auf der Login-Seite. Geben Sie Ihre E-Mail-Adresse ein und Sie erhalten einen Link zum Zurücksetzen Ihres Passworts.'
        }
      ]
    },
    {
      id: 'profile',
      title: 'Profil & Einstellungen',
      icon: <FaUserEdit className="text-secondary" />,
      questions: [
        {
          q: 'Wie ändere ich mein Profilbild (Avatar)?',
          a: 'Gehen Sie zu Ihren Benutzereinstellungen über das Dropdown-Menü bei Ihrem Namen. Dort können Sie ein Bild hochladen oder aus vordefinierten Avataren wählen.'
        },
        {
          q: 'Wie bearbeite ich meine Signatur?',
          a: 'In den Benutzereinstellungen finden Sie den Bereich "Signatur". Hier können Sie BBCode verwenden, um Text zu formatieren. Ihre Signatur erscheint unter jedem Ihrer Beiträge.'
        },
        {
          q: 'Wie ändere ich meine Benachrichtigungseinstellungen?',
          a: 'Unter "Benachrichtigungseinstellungen" können Sie wählen, für welche Ereignisse Sie E-Mail-Benachrichtigungen erhalten möchten: neue PMs, Thread-Antworten, Mentions, etc.'
        },
        {
          q: 'Was sind benutzerdefinierte Felder?',
          a: 'Benutzerdefinierte Felder ermöglichen es Ihnen, zusätzliche Informationen in Ihrem Profil anzuzeigen, wie z.B. Lieblingsteam, Wohnort oder andere persönliche Details.'
        }
      ]
    },
    {
      id: 'threads-posts',
      title: 'Threads & Beiträge',
      icon: <FaComments className="text-accent" />,
      questions: [
        {
          q: 'Wie erstelle ich einen neuen Thread?',
          a: 'Wählen Sie das gewünschte Forum und klicken Sie auf "Neues Thema". Geben Sie einen aussagekräftigen Titel ein und schreiben Sie Ihren Beitrag. Sie können BBCode für Formatierung verwenden.'
        },
        {
          q: 'Wie antworte ich auf einen Thread?',
          a: 'Scrollen Sie zum Ende des Threads und nutzen Sie das Antwortfeld. Sie können auch auf "Zitieren" bei einem bestimmten Beitrag klicken, um darauf zu antworten.'
        },
        {
          q: 'Wie bearbeite ich meine Beiträge?',
          a: 'Klicken Sie auf das Bearbeiten-Symbol (Stift) bei Ihrem Beitrag. Bearbeitungen sind zeitlich begrenzt und werden als "bearbeitet" markiert.'
        },
        {
          q: 'Wie verwende ich BBCode?',
          a: 'BBCode ermöglicht Textformatierung: [b]fett[/b], [i]kursiv[/i], [url]Links[/url], [img]Bilder[/img], [quote]Zitate[/quote]. Eine Vorschau-Funktion hilft beim Formatieren.'
        },
        {
          q: 'Wie kann ich Bilder in Beiträge einfügen?',
          a: 'Verwenden Sie [img]URL-des-Bildes[/img] oder nutzen Sie die Upload-Funktion, falls verfügbar. Bilder sollten web-optimiert und nicht zu groß sein.'
        }
      ]
    },
    {
      id: 'private-messages',
      title: 'Private Nachrichten (PMs)',
      icon: <FaEnvelope className="text-info" />,
      questions: [
        {
          q: 'Wie sende ich eine private Nachricht?',
          a: 'Klicken Sie auf das PM-Symbol in der Navigation oder auf "PM senden" im Profil eines Benutzers. Geben Sie den Empfänger, Betreff und Ihre Nachricht ein.'
        },
        {
          q: 'Wie erhalte ich E-Mail-Benachrichtigungen für neue PMs?',
          a: 'In den Benachrichtigungseinstellungen können Sie E-Mail-Benachrichtigungen für neue private Nachrichten aktivieren. Sie erhalten dann sofort eine E-Mail, wenn eine neue PM ankommt.'
        },
        {
          q: 'Wie lösche ich eine PM-Konversation?',
          a: 'In der PM-Übersicht können Sie einzelne Konversationen löschen. Dies entfernt sie nur für Sie - der andere Teilnehmer kann sie weiterhin sehen.'
        },
        {
          q: 'Gibt es ein Limit für PM-Länge oder -Anzahl?',
          a: 'PMs haben ein Zeichenlimit von 10.000 Zeichen pro Nachricht. Es gibt kein Limit für die Anzahl der Konversationen.'
        }
      ]
    },
    {
      id: 'notifications',
      title: 'Thread-Abonnements & Benachrichtigungen',
      icon: <FaBell className="text-warning" />,
      questions: [
        {
          q: 'Wie abonniere ich einen Thread?',
          a: 'Klicken Sie auf das Glocken-Symbol "Thread abonnieren" in jedem Thread. Sie erhalten dann E-Mail-Benachrichtigungen über neue Antworten.'
        },
        {
          q: 'Wie verwalte ich meine Thread-Abonnements?',
          a: 'Unter "Ungelesene Beiträge" finden Sie alle Threads mit neuen Antworten. Sie können Abonnements dort auch einzeln kündigen.'
        },
        {
          q: 'Wann erhalte ich E-Mail-Benachrichtigungen?',
          a: 'Sie erhalten E-Mails für: neue Antworten in abonnierten Threads, neue private Nachrichten, Mentions (@Benutzername) und andere Ereignisse, die Sie in den Einstellungen aktiviert haben.'
        },
        {
          q: 'Wie deaktiviere ich E-Mail-Benachrichtigungen?',
          a: 'In den Benachrichtigungseinstellungen können Sie alle E-Mail-Typen einzeln deaktivieren oder ganz abstellen. Sie können auch eine Test-E-Mail senden, um Ihre Einstellungen zu überprüfen.'
        }
      ]
    },
    {
      id: 'search',
      title: 'Suche & Navigation',
      icon: <FaSearch className="text-success" />,
      questions: [
        {
          q: 'Wie suche ich nach Beiträgen oder Threads?',
          a: 'Nutzen Sie die Suchfunktion in der oberen Navigation. Sie können nach Stichwörtern, Benutzernamen, Titeln oder Inhalten suchen.'
        },
        {
          q: 'Wie finde ich meine eigenen Beiträge?',
          a: 'Klicken Sie auf Ihren Benutzernamen und dann auf "Beiträge anzeigen" oder nutzen Sie die Suche mit Ihrem Benutzernamen als Filter.'
        },
        {
          q: 'Was sind "Ungelesene Beiträge"?',
          a: 'Diese Funktion zeigt alle Threads mit neuen Antworten seit Ihrem letzten Besuch. Besonders nützlich, um auf dem Laufenden zu bleiben.'
        },
        {
          q: 'Wie markiere ich alle Threads als gelesen?',
          a: 'In der "Ungelesene Beiträge" Ansicht gibt es eine "Alle als gelesen markieren" Funktion, um alle ungelesenen Inhalte auf einmal zu löschen.'
        }
      ]
    },
    {
      id: 'moderation',
      title: 'Meldungen & Moderation',
      icon: <FaFlag className="text-error" />,
      questions: [
        {
          q: 'Wie melde ich einen problematischen Beitrag?',
          a: 'Klicken Sie auf das Melde-Symbol bei einem Beitrag und wählen Sie den Grund (Spam, Beleidigung, etc.). Geben Sie zusätzliche Informationen an, falls nötig.'
        },
        {
          q: 'Was passiert nach einer Meldung?',
          a: 'Moderatoren überprüfen alle Meldungen und entscheiden über entsprechende Maßnahmen (Verwarnung, Beitrag löschen, etc.). Sie erhalten eine Rückmeldung über das Ergebnis.'
        },
        {
          q: 'Welche Inhalte sind nicht erlaubt?',
          a: 'Verboten sind: Beleidigungen, Spam, Diskriminierung, illegale Inhalte, Off-Topic Beiträge und Werbung ohne Genehmigung. Siehe vollständige Forenregeln.'
        }
      ]
    },
    {
      id: 'mobile',
      title: 'Mobile Nutzung',
      icon: <FaMobile className="text-neutral" />,
      questions: [
        {
          q: 'Kann ich das Forum auf dem Smartphone nutzen?',
          a: 'Ja! Das Forum ist vollständig responsive und funktioniert auf allen Geräten. Die mobile Ansicht ist optimiert für Touch-Bedienung.'
        },
        {
          q: 'Gibt es eine App?',
          a: 'Aktuell gibt es keine native App, aber Sie können die Webseite als Progressive Web App (PWA) installieren für eine app-ähnliche Erfahrung.'
        },
        {
          q: 'Funktionieren alle Features auf dem Handy?',
          a: 'Ja, alle Funktionen sind auf mobilen Geräten verfügbar: PMs schreiben, Threads erstellen, Bilder hochladen, etc.'
        }
      ]
    },
    {
      id: 'privacy',
      title: 'Datenschutz & Sicherheit',
      icon: <FaShieldAlt className="text-primary" />,
      questions: [
        {
          q: 'Wie sicher sind meine Daten?',
          a: 'Alle Daten werden verschlüsselt übertragen (HTTPS) und sicher gespeichert. Passwörter werden gehasht und niemals im Klartext gespeichert.'
        },
        {
          q: 'Wer kann meine private Nachrichten lesen?',
          a: 'Nur Sie und der Empfänger können private Nachrichten lesen. Administratoren haben nur in Ausnahmefällen (bei Meldungen) Zugriff.'
        },
        {
          q: 'Kann ich meinen Account löschen?',
          a: 'Ja, kontaktieren Sie einen Administrator für die vollständige Löschung Ihres Accounts und aller damit verbundenen Daten.'
        },
        {
          q: 'Welche Cookies werden verwendet?',
          a: 'Wir verwenden technische Cookies für Login-Sessions und optionale Analytics-Cookies (nur mit Zustimmung) zur Verbesserung des Forums.'
        }
      ]
    },
    {
      id: 'advanced',
      title: 'Erweiterte Features',
      icon: <FaCog className="text-accent" />,
      questions: [
        {
          q: 'Was sind Benutzergruppen?',
          a: 'Benutzergruppen bestimmen Ihre Berechtigungen im Forum (z.B. Moderator, VIP). Einige Gruppen erhalten Sie automatisch, andere auf Anfrage.'
        },
        {
          q: 'Wie funktioniert das Bewertungssystem?',
          a: 'Beiträge können mit Herzchen bewertet werden. Positive Bewertungen erhöhen Ihr Ansehen in der Community.'
        },
        {
          q: 'Was sind Warnungen?',
          a: 'Bei Regelverstößen können Moderatoren Warnungen aussprechen. Mehrere Warnungen können zu temporären oder permanenten Sperren führen.'
        },
        {
          q: 'Gibt es eine API für Entwickler?',
          a: 'Eine öffentliche API ist in Planung. Aktuell ist der Zugriff auf Backend-Funktionen nur für autorisierte Anwendungen möglich.'
        }
      ]
    }
  ];

  const filteredSections = faqSections.map(section => ({
    ...section,
    questions: section.questions.filter(
      q => 
        q.q.toLowerCase().includes(searchTerm.toLowerCase()) ||
        q.a.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(section => section.questions.length > 0);

  return (
    <div className="min-h-screen bg-base-200 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-base-content mb-4">
            Forum FAQ & Hilfe
          </h1>
          <p className="text-xl text-base-content/70 max-w-3xl mx-auto">
            Hier finden Sie Antworten auf die häufigsten Fragen zur Nutzung des tivoli12 Forums. 
            Entdecken Sie alle Features und lernen Sie, wie Sie das Beste aus Ihrer Forum-Erfahrung herausholen.
          </p>
        </div>

        {/* Search */}
        <div className="card bg-base-100 shadow-lg mb-8">
          <div className="card-body">
            <div className="form-control">
              <div className="input-group">
                <input
                  type="text"
                  placeholder="FAQ durchsuchen..."
                  className="input input-bordered flex-1"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <button className="btn btn-square">
                  <FaSearch />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ Sections */}
        <div className="space-y-6">
          {filteredSections.map(section => (
            <div key={section.id} className="card bg-base-100 shadow-lg">
              <div className="card-body">
                <div 
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => toggleSection(section.id)}
                >
                  <div className="flex items-center gap-3">
                    {section.icon}
                    <h2 className="card-title text-2xl">{section.title}</h2>
                    <div className="badge badge-neutral">{section.questions.length}</div>
                  </div>
                  {expandedSection === section.id ? <FaChevronUp /> : <FaChevronDown />}
                </div>
                
                {expandedSection === section.id && (
                  <div className="mt-6 space-y-4">
                    {section.questions.map((qa, index) => (
                      <div key={index} className="bg-base-200 p-4 rounded-lg">
                        <h3 className="font-bold text-lg mb-2 flex items-start gap-2">
                          <span className="badge badge-primary badge-sm mt-1">{index + 1}</span>
                          {qa.q}
                        </h3>
                        <p className="text-base-content/80 leading-relaxed pl-8">{qa.a}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* No Results */}
        {filteredSections.length === 0 && searchTerm && (
          <div className="text-center py-12">
            <div className="text-6xl text-base-content/20 mb-4">🔍</div>
            <h3 className="text-2xl font-bold mb-2">Keine Ergebnisse gefunden</h3>
            <p className="text-base-content/70">
              Versuchen Sie andere Suchbegriffe oder 
              <button 
                className="btn btn-link btn-sm p-0 ml-1"
                onClick={() => setSearchTerm('')}
              >
                alle FAQ anzeigen
              </button>
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="mt-12 text-center">
          <div className="card bg-base-100 shadow-lg">
            <div className="card-body">
              <h3 className="card-title justify-center text-2xl mb-4">
                Weitere Hilfe benötigt?
              </h3>
              <p className="text-base-content/80 mb-4">
                Wenn Sie hier keine Antwort auf Ihre Frage gefunden haben, zögern Sie nicht, uns zu kontaktieren.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <a href="mailto:redaktion@tivoli12.at" className="btn text-white border-none" style={{backgroundColor: '#448f65'}}>
                  <FaEnvelope className="mr-2" />
                  E-Mail senden
                </a>
                <Link to="/forums" className="btn btn-outline text-white hover:text-white hover:border-none" style={{borderColor: '#448f65', color: '#448f65'}} onMouseEnter={(e) => e.target.style.backgroundColor = '#448f65'} onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}>
                  <FaComments className="mr-2" />
                  Im Forum fragen
                </Link>
              </div>
              
              <div className="divider">Schnelle Hilfe</div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <FaHeart className="text-error" />
                  <span>Bei Problemen: Support kontaktieren</span>
                </div>
                <div className="flex items-center gap-2">
                  <FaEye className="text-info" />
                  <span>Forum-Regeln beachten</span>
                </div>
                <div className="flex items-center gap-2">
                  <FaLock className="text-warning" />
                  <span>Sichere Passwörter verwenden</span>
                </div>
                <div className="flex items-center gap-2">
                  <FaBell className="text-success" />
                  <span>Benachrichtigungen aktivieren</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQ;